import React, { useState, useEffect } from 'react';
import {
  IconButton,
  TextField,
  List,
  Typography,
  Box,
  Paper,
  makeStyles,
  useTheme,
  Button
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

import ContactNotesDialogListItem from '../ContactNotesDialogListItem';
import ConfirmationModal from '../ConfirmationModal';
import ContactNotesEditModal from '../ContactNotesEditModal';

import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from '../ButtonWithSpinner';
import useTicketNotes from '../../hooks/useTicketNotes';


import {
  Notes as NotesIcon,
} from "@mui/icons-material";



const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1),
    borderRadius: 8,
  },
  list: {
    width: '100%',
    maxHeight: 200,
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    borderRadius: 8,
  },
  noteField: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  titleRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  }
}));

const NoteSchema = Yup.object().shape({
  note: Yup.string()
    .min(2, "Muito curta")
    .required("Campo obrigatório")
});

export function ContactNotes({ ticket }) {
  const { id: ticketId, contactId } = ticket;
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [, setLoadingList] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showOnDeleteDialog, setShowOnDeleteDialog] = useState(false);
  const [selectedNote, setSelectedNote] = useState({});
  const [notes, setNotes] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [showAddField, setShowAddField] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const { saveNote, editNote, deleteNote, listNotes } = useTicketNotes();

  const PAGE_SIZE = 5;

  useEffect(() => {
    fetchNotes({ page: 1, reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, ticketId]);

  const handleEdit = (note) => {
    setEditingNote({ ...note });
  };

  const handleSave = async values => {
    setLoading(true);
    try {
      const payload = {
        note: values.note,
        ticketId: editingNote?.ticketId || ticketId,
        contactId,
        id: editingNote?.id
      };

      if (editingNote?.id) {
        await editNote(payload);
      } else {
        await saveNote(payload);
      }

  await fetchNotes({ page: 1, reset: true });
      setEditingNote(null);
      setShowAddField(false);
      toast.success('Observação salva com sucesso!');
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const handleOpenDialogDelete = (item) => {
    setSelectedNote(item);
    setShowOnDeleteDialog(true);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
  await deleteNote(selectedNote.id);
  await fetchNotes({ page: 1, reset: true });
      setSelectedNote({});
      toast.success('Observação excluída com sucesso!');
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const fetchNotes = async ({ page = 1, reset = false } = {}) => {
    const append = !reset && page > 1;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoadingList(true);
    }

    try {
      const response = await listNotes({
        contactId,
        pageNumber: page,
        pageSize: PAGE_SIZE
      });

      const fetchedNotes = response?.notes || [];
      setNotes(prev => {
        if (reset || !append) {
          return fetchedNotes;
        }
        const existingIds = new Set(prev.map(item => item.id));
        const merged = [...prev];
        fetchedNotes.forEach(item => {
          if (!existingIds.has(item.id)) {
            merged.push(item);
          }
        });
        return merged;
      });
      setHasMore(Boolean(response?.hasMore));
      setPageNumber(page);
    } catch (e) {
      toast.error(e);
    } finally {
      setLoadingList(false);
      setLoadingMore(false);
    }
  };

  return (
    <Paper className={classes.container} elevation={0}>
      <ContactNotesEditModal
        open={editingNote !== null}
        onClose={() => setEditingNote(null)}
        note={editingNote ? editingNote.note : ''}
        onSave={(noteText) => handleSave({ note: noteText })}
      />

      <ConfirmationModal
        title="Excluir Registro"
        open={showOnDeleteDialog}
        onClose={setShowOnDeleteDialog}
        onConfirm={handleDelete}
      >
        Deseja realmente excluir este registro?
      </ConfirmationModal>

      {/* Título + botão de adicionar */}
      <Box className={classes.titleRow}>
      <NotesIcon fontSize="small" />

        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {i18n.t("ticketOptionsMenu.appointmentsModal.title") || "Observações do contato"}
        </Typography>

        {!showAddField && (
          <IconButton
            onClick={() => setShowAddField(true)}
            color="primary"
            size="small"
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {/* Campo de nova anotação */}
      {showAddField && (
        <Formik
          initialValues={{ note: "" }}
          validationSchema={NoteSchema}
          onSubmit={async (values, actions) => {
            await handleSave(values);
            actions.resetForm();
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, setErrors }) => (
            <Form>
              <Field
                as={TextField}
                name="note"
                rows={3}
                label={i18n.t("ticketOptionsMenu.appointmentsModal.textarea")}
                placeholder={i18n.t("ticketOptionsMenu.appointmentsModal.placeholder")}
                multiline
                variant="outlined"
                fullWidth
                error={touched.note && Boolean(errors.note)}
                helperText={touched.note && errors.note}
                className={classes.noteField}
              />

              <Box className={classes.buttonGroup}>
                <IconButton
                  onClick={() => {
                    setShowAddField(false);
                    setErrors({});
                  }}
                  color="secondary"
                >
                  <ClearIcon />
                </IconButton>
                <ButtonWithSpinner
                  loading={loading}
                  color="primary"
                  type="submit"
                  variant="contained"
                  size="small"
                >
                  <CheckIcon />
                </ButtonWithSpinner>
              </Box>
            </Form>
          )}
        </Formik>
      )}

      {/* Lista de notas, ordenadas da mais recente para mais antiga */}
      {notes.length > 0 && (
        <>
          <List className={classes.list}>
            {[...notes]
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map(note => (
                <ContactNotesDialogListItem
                  key={note.id}
                  note={note}
                  deleteItem={handleOpenDialogDelete}
                  editItem={handleEdit}
                />
              ))}
          </List>
          {hasMore && (
            <Box display="flex" justifyContent="center" mt={1}>
              <Button
                onClick={() => fetchNotes({ page: pageNumber + 1 })}
                disabled={loadingMore}
                color="primary"
                variant="outlined"
              >
                {loadingMore ? 'Carregando...' : 'Carregar mais'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
